import Box from '@mui/material/Box';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useEffect, useRef } from 'react';
import { UISitterDisplay } from 'types/ui.sitter.types';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

interface MapViewProps {
  sitters: UISitterDisplay[];
  selectedSitterId?: string | null;
  onSitterSelect?: (sitterId: string) => void;
  userLocation?: { lat: number; lng: number } | null;
  panTo?: { lat: number; lng: number } | null;
}

const MapView: React.FC<MapViewProps> = ({
  sitters,
  selectedSitterId,
  onSitterSelect,
  userLocation,
  panTo,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const onSitterSelectRef = useRef(onSitterSelect);
  const mapLoadedRef = useRef(false);
  const sittersRef = useRef(sitters);
  const selectedSitterIdRef = useRef(selectedSitterId);

  useEffect(() => {
    onSitterSelectRef.current = onSitterSelect;
  }, [onSitterSelect]);

  useEffect(() => {
    sittersRef.current = sitters;
  }, [sitters]);

  useEffect(() => {
    selectedSitterIdRef.current = selectedSitterId;
  }, [selectedSitterId]);

  // Register global view profile handler
  useEffect(() => {
    (window as any).happypawsViewProfile = (sitterId: string) => {
      window.location.href = `/sitter/${sitterId}`;
    };
    return () => {
      delete (window as any).happypawsViewProfile;
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-80.1918, 25.7617],
      zoom: 11,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.on('load', () => {
      mapLoadedRef.current = true;

      // Add source with current sitters
      map.addSource('sitters', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: sittersRef.current.map((s) => ({
            type: 'Feature',
            properties: {
              id: s.id,
              name: s.name,
              price: s.price,
              label: `$${s.price}`,
              selected: s.id === selectedSitterIdRef.current,
            },
            geometry: {
              type: 'Point',
              coordinates: [s.coordinates.lng, s.coordinates.lat],
            },
          })),
        },
      });

      // Circle layer
      map.addLayer({
        id: 'sitters-circle',
        type: 'circle',
        source: 'sitters',
        paint: {
          'circle-radius': 22,
          'circle-color': ['case', ['==', ['get', 'selected'], true], '#178B8E', '#50D9D7'],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Price label layer
      map.addLayer({
        id: 'sitters-label',
        type: 'symbol',
        source: 'sitters',
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 13,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Click
      map.on('click', 'sitters-circle', (e) => {
        if (!e.features || !e.features[0]) return;
        const feature = e.features[0];
        const coords = (feature.geometry as any).coordinates.slice() as [number, number];
        const { name, price, id } = feature.properties as any;

        onSitterSelectRef.current?.(id);

        new mapboxgl.Popup({ closeButton: false, maxWidth: '200px' })
          .setLngLat(coords)
          .setHTML(
            `
            <div style="font-family: sans-serif; padding: 10px 12px; min-width: 160px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <div style="width: 36px; height: 36px; border-radius: 50%; background-color: #178B8E; display: flex; align-items: center; justify-content: center; color: white; font-weight: 700; font-size: 14px;">
                  ${name.charAt(0)}
                </div>
                <div>
                  <div style="font-weight: 700; font-size: 14px; color: #1A1A1A;">${name}</div>
                  <div style="font-size: 12px; color: #5A5A5A;">$${price} / night</div>
                </div>
              </div>
              <button onclick="window.happypawsViewProfile('${id}')" style="width: 100%; padding: 7px 0; background-color: #178B8E; color: white; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer;">
                View Profile
              </button>
            </div>
          `
          )
          .addTo(map);
      });

      // Cursor
      map.on('mouseenter', 'sitters-circle', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'sitters-circle', () => {
        map.getCanvas().style.cursor = '';
      });
    });

    mapRef.current = map;

    return () => {
      mapLoadedRef.current = false;
      map.remove();
    };
  }, []);

  // Update source when sitters or selection changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoadedRef.current) return;

    const source = map.getSource('sitters') as mapboxgl.GeoJSONSource;
    if (!source) return;

    source.setData({
      type: 'FeatureCollection',
      features: sitters.map((s) => ({
        type: 'Feature',
        properties: {
          id: s.id,
          name: s.name,
          price: s.price,
          label: `$${s.price}`,
          selected: s.id === selectedSitterId,
        },
        geometry: {
          type: 'Point',
          coordinates: [s.coordinates.lng, s.coordinates.lat],
        },
      })),
    });
  }, [sitters, selectedSitterId]);

  // Fly to user location
  useEffect(() => {
    if (!mapRef.current || !userLocation) return;
    mapRef.current.flyTo({
      center: [userLocation.lng, userLocation.lat],
      zoom: 12,
      duration: 1500,
    });
  }, [userLocation]);

  // Fly to searched location
  useEffect(() => {
    if (!mapRef.current || !panTo) return;
    mapRef.current.flyTo({
      center: [panTo.lng, panTo.lat],
      zoom: 12,
      duration: 1500,
    });
  }, [panTo]);

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        flex: 1,
        height: '100%',
        position: 'relative',
      }}
    />
  );
};

export default MapView;
